import { Rate } from "@redotech/fx";
import { LoadState } from "@redotech/react-util/load";
import { useEffect, useMemo, useState } from "react";
import { useFx } from "./fx-context";
import { WatchRateRef } from "./fx-subscription-store";

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type { WatchRateRef } from "./fx-subscription-store";

/**
 * Subscribe to a live rate through the provider's shared subscription store.
 * Pass `undefined` to watch nothing — the hook stays inert and resolves to a
 * non-pending empty state.
 */
export function useWatchRate(ref: WatchRateRef | undefined): LoadState<Rate> {
  const { subscribe } = useFx();
  const [state, setState] = useState<LoadState<Rate>>({
    pending: ref !== undefined,
  });

  useEffect(() => {
    if (!ref) {
      setState({ pending: false });
      return;
    }
    setState({ pending: true });
    return subscribe(
      { base: ref.base, quote: ref.quote, source: ref.source },
      setState,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.base, ref?.quote, ref?.source, subscribe]);

  return state;
}

const refKey = (r: WatchRateRef) => `${r.source}:${r.base}:${r.quote}`;

/**
 * Watch several rates at once (the set may change between renders). Returns a
 * map keyed by `source:base:quote`; absent key = still pending.
 */
export function useWatchRates(refs: WatchRateRef[]): Map<string, Rate> {
  const { subscribe } = useFx();
  const [rates, setRates] = useState<Map<string, Rate>>(new Map());
  const key = refs.map(refKey).sort().join("|");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableRefs = useMemo(() => refs, [key]);

  useEffect(() => {
    setRates(new Map());
    const unsubscribes = stableRefs.map((ref) =>
      subscribe(ref, (state) => {
        setRates((prev) => {
          const next = new Map(prev);
          // Mirror the latest state exactly: a subscription that reverts to
          // pending/error must not leave its stale rate behind.
          if (state.value) {
            next.set(refKey(ref), state.value);
          } else {
            next.delete(refKey(ref));
          }
          return next;
        });
      }),
    );
    return () => {
      for (const u of unsubscribes) {
        u();
      }
    };
  }, [stableRefs, subscribe]);

  return rates;
}
